import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTButton from '../common/RTButton';
import UpdateAllocationStatusModal from './UpdateAllocationStatusModal';
import { apiGet } from '../../api/apiClient';
import { navigateToPath } from '../../utils/navigationHelper';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { getStatusDisplay } from '../../utils/allocationStatusUtils';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import './ViewAllocatedBeds.css';
import RTPageLayout from '../common/RTPageLayout';
import RTStatusPill from '../common/statusPill';

const ViewAllocatedBeds = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [bedSummary, setBedSummary] = useState({
    total_beds: 0,
    total_allocated: 0,
    total_vacant: 0
  });
  const [allocations, setAllocations] = useState([]);
  const [error, setError] = useState(null);
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    mode: '', // 'start', 'cancel', 'end'
    allocation: null
  });
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [filters, setFilters] = useState({
    building_id: undefined,
    room_type: undefined,
    allocation_status: undefined,
    has_end_date: undefined,
    occupancy_gender: undefined,
    search: '',
  });

  const filterFields = useMemo(() => [
    {
      key: 'building_id',
      label: 'Building',
      type: 'autocomplete',
      width: 'medium',
      placeholder: 'Search building...',
      options: buildingOptions,
      applyImmediately: true,
    },
    {
      key: 'room_type',
      label: 'Room Type',
      type: 'dropdown',
      width: 'compact',
      options: [
        { value: '', label: 'All' },
        { value: 'AC', label: 'AC' },
        { value: 'Non AC', label: 'Non AC' },
      ],
      applyImmediately: true,
    },
    {
      key: 'occupancy_gender',
      label: 'Gender',
      type: 'dropdown',
      width: 'compact',
      options: OCCUPANCY_GENDER_FILTER_OPTIONS,
      applyImmediately: true,
    },
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      width: 'wide',
      placeholder: 'PG name, mobile, or address...',
      applyImmediately: false,
    },
    {
      key: 'allocation_status',
      label: 'Status',
      type: 'dropdown',
      width: 'medium',
      options: [
        { value: '', label: 'All' },
        { value: 'FutureAllocation', label: 'Future Allocation' },
        { value: 'Allocated', label: 'Allocated' },
      ],
      applyImmediately: true,
    },
    {
      key: 'has_end_date',
      label: 'End Date',
      type: 'dropdown',
      width: 'compact',
      options: [
        { value: '', label: 'All' },
        { value: 'yes', label: 'With End Date' },
        { value: 'no', label: 'Without End Date' },
      ],
      applyImmediately: true,
    },
  ], [buildingOptions]);

  useEffect(() => {
    fetchData(filters);
    loadBuildingOptions();
  }, []);

  const loadBuildingOptions = async () => {
    try {
      const response = await apiGet('/building/names?status=Active');
      setBuildingOptions(
        (response.data || []).map(b => ({
          value: String(b.building_id),
          label: b.name,
        }))
      );
    } catch (error) {
      console.error('Error loading buildings:', error);
    }
  };

  const fetchData = async (filterParams = filters) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch bed summary statistics
      const bedResponse = await apiGet('/beds');
      setBedSummary(bedResponse.data.summary);

      // Fetch allocation data ordered by building, room, then bed
      const params = new URLSearchParams();
      params.append(
        'status',
        filterParams.allocation_status || 'FutureAllocation,Allocated'
      );
      params.append('order_by', 'building');
      if (filterParams.building_id) {
        params.append('building_id', filterParams.building_id);
      }
      if (filterParams.room_type) {
        params.append('room_type', filterParams.room_type);
      }
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      if (filterParams.search?.trim()) {
        params.append('search', filterParams.search.trim());
      }
      const allocationResponse = await apiGet(`/allocation?${params.toString()}`);
      setAllocations(allocationResponse.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load allocation data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    if (key === 'building_id' && (value === undefined || value === '')) {
      setBuildingSearch('');
    }
    const next = {
      ...filters,
      [key]: key === 'search' ? (value ?? '') : (value || undefined),
    };
    setFilters(next);
    if (key === 'has_end_date') {
      return;
    }
    if (applyImmediately !== false) {
      fetchData(next);
    }
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleFilterSearchChange = (key, text) => {
    if (key === 'building_id') {
      setBuildingSearch(text);
      if (!text) {
        const next = { ...filters, building_id: undefined };
        setFilters(next);
        fetchData(next);
      }
    }
  };

  const displayedAllocations = useMemo(() => {
    if (filters.has_end_date === 'yes') {
      return allocations.filter(allocation => allocation.end_date);
    }
    if (filters.has_end_date === 'no') {
      return allocations.filter(allocation => !allocation.end_date);
    }
    return allocations;
  }, [allocations, filters.has_end_date]);

  const STATUS_FILTER_LABELS = {
    FutureAllocation: 'Future Allocation',
    Allocated: 'Allocated',
  };

  const END_DATE_FILTER_LABELS = {
    yes: 'With End Date',
    no: 'Without End Date',
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (filters.building_id) {
      const buildingLabel = buildingSearch
        || buildingOptions.find(o => o.value === String(filters.building_id))?.label
        || filters.building_id;
      pills.push({ key: 'building_id', label: `Building: ${buildingLabel}` });
    }
    if (filters.room_type) {
      pills.push({ key: 'room_type', label: `Room Type: ${filters.room_type}` });
    }
    if (filters.occupancy_gender) {
      const genderLabel = OCCUPANCY_GENDER_FILTER_OPTIONS.find(
        o => o.value === filters.occupancy_gender
      )?.label || filters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    if (filters.search?.trim()) {
      pills.push({ key: 'search', label: `Search: ${filters.search.trim()}` });
    }
    if (filters.allocation_status) {
      pills.push({
        key: 'allocation_status',
        label: `Status: ${STATUS_FILTER_LABELS[filters.allocation_status] || filters.allocation_status}`,
      });
    }
    if (filters.has_end_date) {
      pills.push({
        key: 'has_end_date',
        label: `End Date: ${END_DATE_FILTER_LABELS[filters.has_end_date] || filters.has_end_date}`,
      });
    }
    return pills;
  }, [filters, buildingSearch, buildingOptions]);

  const handleRemoveAppliedFilter = (key) => {
    if (key === 'building_id') {
      setBuildingSearch('');
    }
    const next = {
      ...filters,
      [key]: key === 'search' ? '' : undefined,
    };
    setFilters(next);
    if (key !== 'has_end_date') {
      fetchData(next);
    }
  };

  const handleClearAllAppliedFilters = () => {
    setBuildingSearch('');
    const next = {
      building_id: undefined,
      room_type: undefined,
      allocation_status: undefined,
      has_end_date: undefined,
      occupancy_gender: undefined,
      search: '',
    };
    setFilters(next);
    fetchData(next);
  };

  // Check if start date is today or in the past
  const isStartDateReached = (startDate) => {
    if (!startDate) return false;
    const today = new Date();
    const start = new Date(startDate);
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    return start <= today;
  };

  // Check if end date is in the past
  const isEndDatePassed = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    const end = new Date(endDate);
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end < today;
  };

  // Handle start allocation
  const handleStartAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'start',
      allocation: allocation
    });
  };

  // Handle cancel allocation
  const handleCancelAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'cancel',
      allocation: allocation
    });
  };

  // Handle end allocation
  const handleEndAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'end',
      allocation: allocation
    });
  };

  // Update allocation status
  const updateAllocationStatus = async () => {
    try {
      setLoading(true);

      // Refresh data after update
      await fetchData(filters);

      // Close modal
      setStatusModal({ isOpen: false, mode: '', allocation: null });

    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal success
  const handleModalSuccess = async () => {
    // Refresh data and close modal
    await fetchData(filters);
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Close modal
  const closeModal = () => {
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Calculate beds vacating in next 30 days
  const getBedsVacatingNext30Days = () => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return displayedAllocations.filter(allocation => {
      if (!allocation.end_date) return false;
      const endDate = new Date(allocation.end_date);
      const today = new Date();
      return endDate >= today && endDate <= thirtyDaysFromNow;
    }).length;
  };

  // Calculate total monthly rent
  const getTotalMonthlyRent = () => {
    return displayedAllocations.reduce((total, allocation) => {
      return total + (allocation.rent_amount || 0);
    }, 0);
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const handleViewAllocation = (allocationId) => {
    navigateToPath(`/bedallocation/allocation/${allocationId}`);
  };

  // Define table columns (building and room first for building-wise view)
  const columns = [
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handleBuildingClick(row.building_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Room',
      accessor: 'room_name'
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handlePGClick(row.pg_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Mobile',
      accessor: 'pg_mobile'
    },
    {
      Header: 'Start Date',
      accessor: 'start_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'End Date',
      accessor: 'end_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'Advance Amount',
      accessor: 'advance_amount',
      Cell: ({ row }) => {
        const advanceAmount = row.advance_amount || 0;
        const advanceRemaining = row.advance_remaining || 0;

        // Show red X if advance is 0 or if there's remaining advance
        // Show green tick if advance > 0 and no remaining advance
        const showRedX = advanceAmount === 0 || advanceRemaining > 0;
        const hasRemaining = advanceRemaining > 0;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>₹{advanceAmount.toLocaleString()}</span>
            {showRedX ? (
              <span style={{ color: '#d32f2f', fontSize: '18px', fontWeight: 'bold' }} title="Advance not fully paid">✗</span>
            ) : (
              <span style={{ color: '#4caf50', fontSize: '18px', fontWeight: 'bold' }} title="Advance fully paid">✓</span>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Rent Amount',
      accessor: 'rent_amount',
      Cell: ({ value }) => value ? `₹${value.toLocaleString()}` : 'N/A'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <RTStatusPill status={value}>{getStatusDisplay(value)}</RTStatusPill>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const isFutureAllocation = row.status === 'FutureAllocation';
        const isAllocated = row.status === 'Allocated';
        const canStart = isFutureAllocation && isStartDateReached(row.start_date);
        const canEnd = isAllocated && row.end_date && isEndDatePassed(row.end_date);

        return (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {canStart && (
              <RTButton
                type="link"
                permissionKey="bedmanagement.allocate_bed"
                onClick={() => handleStartAllocation(row)}
                style={{ fontWeight: 'bold' }}
              >
                Start Allocation
              </RTButton>
            )}

            {canEnd && (
              <RTButton
                type="link"
                permissionKey="bedmanagement.allocate_bed"
                onClick={() => handleEndAllocation(row)}
                style={{ fontWeight: 'bold' }}
              >
                End Allocation
              </RTButton>
            )}

            {isFutureAllocation && (
              <RTButton
                type="link"
                permissionKey="bedmanagement.allocate_bed"
                onClick={() => handleCancelAllocation(row)}
                style={{ fontWeight: 'bold' }}
              >
                Cancel Allocation
              </RTButton>
            )}

            <RTButton
              type="link"
              onClick={() => handleViewAllocation(row.allocation_id)}
              style={{ fontWeight: 'bold' }}
            >
              View
            </RTButton>
          </div>
        );
      }
    }
  ];

  return (
    <>
      <RTPageLayout
        activePath={location.pathname}
        title="View Allocated Beds"
        actions={<>
          <RTButton variant="secondary" onClick={() => window.history.back()}>
            &#8592; Back
          </RTButton>
        </>}
      >
        {error && (
          <div className="rt-error-message">
            {error}
            <RTButton
              type="primary"
              onClick={() => fetchData(filters)}
              style={{ marginLeft: '10px' }}
            >
              Retry
            </RTButton>
          </div>
        )}

        {/* Summary Statistics */}
        <div className="rt-summary-section">
          <div className="rt-summary-table">
            <h4 style={{ color: '#153C7F', marginBottom: '16px' }}>Bed Allocation Summary</h4>
            <div>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Beds:</label>
                  <span>{bedSummary.total_beds}</span>
                </div>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Allocated Beds:</label>
                  <span>{bedSummary.total_allocated}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Vacant Beds:</label>
                  <span>{bedSummary.total_vacant}</span>
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Occupancy Rate:</label>
                  <span>
                    {bedSummary.total_beds > 0
                      ? `${Math.round((bedSummary.total_allocated / bedSummary.total_beds) * 100)}%`
                      : '0%'
                    }
                  </span>
                </div>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Beds Vacating in Next 30 Days:</label>
                  <span>{getBedsVacatingNext30Days()}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Monthly Rent:</label>
                  <span>₹{getTotalMonthlyRent().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <RTFilteredTable
          loading={loading}
          loadingMessage="Loading allocation data..."
          filters={
            <RTFilters
              fields={filterFields}
              values={filters}
              searchValues={{ building_id: buildingSearch }}
              onChange={handleFilterChange}
              onSearchChange={handleFilterSearchChange}
              onApply={handleApplyFilters}
              pills={appliedFilterPills}
              onRemovePill={handleRemoveAppliedFilter}
              onClearAll={handleClearAllAppliedFilters}
            />
          }
        >
          <Table
            title="Allocation Details"
            columns={columns}
            data={displayedAllocations}
            showSearch={false}
            exportable={true}
            exportFileName="allocated_beds"
            showPagination={true}
            noDataText="No bed allocations found."
            disableSort={true}
            rowGroupConfig={{
              groups: [
                { key: 'room_id', columns: ['room_name'] }
              ]
            }}
          />
        </RTFilteredTable>
      </RTPageLayout>

      {/* Status Update Modal */}
      <UpdateAllocationStatusModal
        isOpen={statusModal.isOpen}
        mode={statusModal.mode}
        allocation={statusModal.allocation}
        onSuccess={handleModalSuccess}
        onCancel={closeModal}
      />
    </>
  );
};

export default ViewAllocatedBeds;